import { useState } from "react";
import { Pet } from "../../../common/types";
import { formatTimerCardTime, getPassesIcon, getPetTypeFlags, getTrainingStartAndEndTime } from "../../helpers/helpers";
import { Meter } from "../utils/Meter";
import { DarkMeter } from "../utils/DarkMeter";
import { serverURL } from "../../helpers/utils";

export function PetPassCard({
  pet,
  walletAddress,
  isDarkMode,
}: {
  pet: Pet;
  walletAddress: string;
  isDarkMode: boolean;
}) {
  const [showTooltip, setShowTooltip] = useState(false);

  const toggleTooltip = () => setShowTooltip(!showTooltip);

  const statusIcon = getPassesIcon(
    pet.timeUntilStarving,
    pet.timeUntilWheel,
    pet.atkTrainingEndsTimestamp,
    pet.defTrainingEndsTimestamp,
    pet.shieldExpires,
  );

  const { trainingStartSec, trainingEndSec } = getTrainingStartAndEndTime(pet);
  const { isZFren, isDMonkey, isGDog, isFPanda, isEPepe } = getPetTypeFlags(pet.petType);
  const timeUntilStarving = formatTimerCardTime(pet.timeUntilStarving, "starving");
  const timeUntilAttack = formatTimerCardTime(pet.timeUntilAttack, "attack");
  const timeUntilWheel = formatTimerCardTime(pet.timeUntilWheel, "wheel");
  const timeUntilShieldExpires = formatTimerCardTime(pet.shieldExpires, "shield");
  const timeTrainingEnds = formatTimerCardTime(trainingEndSec, "training");
  const availableBonks = pet.availableBonks;
  //  const petStatus = pet.status;
  const passUntil = formatTimerCardTime(pet.passUntil, "expiring");
  const MeterComponent = isDarkMode ? DarkMeter : Meter;

  // const hibernating = petStatus === 5;

  return (
    <div
      className={`mb-2 ml-4 mr-4 mt-2 flex h-[15rem] w-[21.5rem] flex-col overflow-hidden rounded-lg border-2 border-brown bg-white [box-shadow:inset_0_-2px_#342e2e] dark:border-darkgreen dark:bg-darkblue dark:[box-shadow:inset_0_-2px_#37b764]`}
    >
      <div className="relative h-full w-full">
        <div
          className="h-[7rem] w-full bg-cover bg-center"
          style={{ backgroundImage: `url('/imgs/fpBkg.png')` }}
        >
          {statusIcon && (
            <span
              className={`absolute ml-[12.7rem] mt-[3.2rem] flex -translate-y-1/3 translate-x-1/3 transform rounded-full border px-2 py-[.2rem] text-xs ring-1 ring-inset
                ${
                  isDarkMode
                    ? "border-darkgreen bg-darkblue ring-darkgreen [box-shadow:inset_0_-2px_#37b764]"
                    : "border-brown bg-green ring-brown [box-shadow:inset_0_-2px_#342e2e]"
                }`}
            >
              <img
                src={isDarkMode ? statusIcon.replace(".svg", "Dark.svg") : statusIcon}
                alt=""
                className={`h-4 w-4
                  ${statusIcon.includes("weight") ? "mb-[.1rem] h-[.75rem] w-[.85rem]" : ""}
                  ${statusIcon.includes("apple") ? "mb-[.1rem] h-[.75rem] w-[.85rem]" : ""}
                  ${statusIcon.includes("shield-solid") ? "mb-[.1rem] h-[.75rem] w-[.75rem]" : ""}
                `}
              />
            </span>
          )}
          <span className="absolute mt-4 -translate-y-1/3 translate-x-1/3 transform rounded-full border border-brown bg-green px-2 py-[.1rem] text-xs ring-1 ring-inset ring-brown [box-shadow:inset_0_-2px_#342e2e]  dark:border-darkgreen dark:bg-darkblue dark:ring-darkgreen dark:[box-shadow:inset_0_-2px_#37b764]">
            lv. {pet.level}
          </span>
          <span className="absolute mt-[3.2rem] flex -translate-y-1/3 translate-x-1/3 transform flex-row rounded-full border border-brown bg-green py-[.1rem] pl-[.2rem] pr-[.45rem] text-xs ring-1 ring-inset ring-brown [box-shadow:inset_0_-2px_#342e2e] dark:border-darkgreen dark:bg-darkblue dark:ring-darkgreen dark:[box-shadow:inset_0_-2px_#37b764]">
            <img
              src={isDarkMode ? "/imgs/swordDark.svg" : "/imgs/sword.svg"}
              alt=""
              className="mb-[.05rem] mr-[.15rem] h-4 w-4 scale-x-[-1] transform"
            />{" "}
            {pet.attackPoints}
          </span>
          <span className="absolute mt-[5.5rem] flex -translate-y-1/3 translate-x-1/3 transform flex-row rounded-full border border-brown bg-green py-[.1rem] pl-[.2rem] pr-[.4rem] text-xs ring-1 ring-inset ring-brown [box-shadow:inset_0_-2px_#342e2e] dark:border-darkgreen dark:bg-darkblue dark:ring-darkgreen dark:[box-shadow:inset_0_-2px_#37b764]">
            <img
              src={isDarkMode ? "/imgs/shieldDark.svg" : "/imgs/shield.svg"}
              alt=""
              className="ml-[.25rem] mr-[.175rem] mt-[.1225rem] h-3 w-3"
            />{" "}
            {pet.defensePoints}
          </span>
          <span className="absolute ml-[15.25rem] mt-4 flex -translate-y-1/3 translate-x-1/3 transform flex-row rounded-full border border-brown bg-green py-[.1rem] pl-[.47rem]  pr-[.5rem] text-xs ring-1 ring-inset ring-brown [box-shadow:inset_0_-2px_#342e2e] dark:border-darkgreen dark:bg-darkblue dark:ring-darkgreen dark:[box-shadow:inset_0_-2px_#37b764]">
            <img
              src={isDarkMode ? "/imgs/ethereumDark.svg" : "/imgs/ethereum.svg"}
              alt=""
              className="mr-[.15rem] mt-[.02rem] h-[.85rem] w-[.85rem]"
            />{" "}
            {pet.rewards.toFixed(3)}
          </span>
          <span className="absolute ml-[15.53rem] mt-[3.2rem] flex -translate-y-1/3 translate-x-1/3 transform flex-row rounded-full border border-brown bg-green px-2 py-[.1rem] text-xs ring-1 ring-inset ring-brown [box-shadow:inset_0_-2px_#342e2e] dark:border-darkgreen dark:bg-darkblue dark:ring-darkgreen dark:[box-shadow:inset_0_-2px_#37b764]">
            <img
              src={isDarkMode ? "/imgs/hourglassDark.svg" : "/imgs/hourglass.svg"}
              alt=""
              className="mr-[.15rem] mt-[.1rem] h-[.75rem] w-[.75rem]"
            />{" "}
            {pet.petAgeInDays}
          </span>
          <span className="absolute ml-[15.25rem] mt-[5.5rem] flex -translate-y-1/3 translate-x-1/3 transform flex-row rounded-full border border-brown bg-green py-[.1rem] pl-[.4rem] pr-[.5rem] text-xs ring-1 ring-inset ring-brown [box-shadow:inset_0_-2px_#342e2e] dark:border-darkgreen dark:bg-darkblue dark:ring-darkgreen dark:[box-shadow:inset_0_-2px_#37b764]">
            <img
              src={isDarkMode ? "/imgs/compassDark.svg" : "/imgs/compass.svg"}
              alt=""
              className="mr-[.15rem] mt-[.1rem] h-[.75rem] w-[.75rem]"
            />{" "}
            {pet.explorerPoints}
          </span>
          <div className="flex h-full items-center justify-center">
            <a href={`https://frenpet.xyz/pet/${pet.id}`} target="_blank" rel="noreferrer">
              <div
                className={`${
                  isZFren
                    ? "ml-5 rounded-lg border-2 border-black bg-green"
                    : isEPepe
                      ? "rounded-lg border-2 border-black bg-green"
                      : ""
                }`}
              >
                <img
                  src={`${serverURL}/svgs/${pet.svgPath}`}
                  alt={"no pet img"}
                  style={isEPepe ? { mixBlendMode: "multiply" } : undefined}
                  className={`object-cover ${
                    isZFren || isEPepe
                      ? "h-[3.25rem] w-[3.25rem]"
                      : isDMonkey || isGDog || isFPanda
                        ? "ml-5 h-[3.5rem] w-[3.5rem]"
                        : "ml-5 h-[6rem] w-[6rem]"
                  }`}
                />
              </div>
            </a>
          </div>
        </div>
      </div>
      <div
        className={`w-full border-b-4 ${isDarkMode ? "border-darkgreen" : "border-brown"}`}
      ></div>
      <div
        className={`relative ml-9 mr-3 mt-2 flex flex-row justify-between ${isDarkMode ? "text-white" : "text-brown"}`}
      >
        <h2 className="text-md flex max-w-[5.5rem] items-center overflow-hidden text-ellipsis whitespace-nowrap">
          <img
            src={isDarkMode ? "/imgs/piggy-bankDark.svg" : "/imgs/piggy-bank.svg"}
            alt=""
            className="mr-1 h-4  w-4"
          />
          <div className="min-w-20">{pet.score?.toFixed(0)}</div>
        </h2>

        <h2 className="text-md mr-[1.75rem] flex items-center justify-center overflow-hidden truncate text-ellipsis whitespace-nowrap">
          {pet.name.length > 11 ? `${pet.name.slice(0, 6)}...` : pet.name}
        </h2>

        <h2 className="text-md flex items-center overflow-hidden text-ellipsis whitespace-nowrap">
          {pet.availableGacha > 0 && (
            <span
              className={`absolute -translate-y-[40%] translate-x-[15%] transform rounded-full border px-[.35rem] py-[.05rem] text-xs ring-1 ring-inset ${isDarkMode ? "border-darkgreen bg-darkblue ring-darkgreen [box-shadow:inset_0_-2px_#37b764]" : "border-brown bg-green ring-brown [box-shadow:inset_0_-2px_#342e2e]"}`}
            >
              {pet.availableGacha}
            </span>
          )}
          <img
            src={isDarkMode ? "/imgs/slotDark.svg" : "/imgs/slot.svg"}
            alt=""
            className="mr-1 h-4 w-4"
          />
          <div>{pet.nextGacha.toFixed(0)}</div>
        </h2>
      </div>

      <div className="relative flex flex-col justify-start">
        <div className="flex h-full w-full flex-col p-2">
          {showTooltip && (
            <div
              className={`absolute left-[5%] top-[-122%] z-20 mt-2 w-[8.3rem] rounded border-2 px-2 py-1 text-brown ${isDarkMode ? "border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]" : "border-brown bg-white text-brown [box-shadow:inset_0_-2px_#342e2e]"}`}
            >
              <div className="flex">
                <img
                  src={isDarkMode ? "/imgs/skullDark.svg" : "/imgs/skull.svg"}
                  alt=""
                  className=" ml-1 mr-1 mt-[0.22rem] h-4 w-4"
                />
                • {timeUntilStarving}
              </div>
              <div className="mt-2 flex">
                <img
                  src={isDarkMode ? "/imgs/swordDark.svg" : "/imgs/sword.svg"}
                  alt=""
                  className=" z-10 mt-[0.05rem] h-6 w-6 scale-x-[-1] transform"
                />
                • {timeUntilAttack}
              </div>
              <div className="mt-2 flex">
                <img
                  src={isDarkMode ? "/imgs/boltDark.svg" : "/imgs/bolt.svg"}
                  alt=""
                  className=" z-10 mt-[0.05rem] h-5 w-5 "
                />
                • {availableBonks} bonks
              </div>
              <div className="mt-2 flex">
                <img
                  src={isDarkMode ? "/imgs/wheelspinDark.svg" : "/imgs/wheelspin.svg"}
                  alt=""
                  className=" mr-1 mt-[0.11rem] h-5 w-5 scale-x-[-1] transform"
                />
                • {timeUntilWheel}
              </div>
              <div className="mt-2 flex">
                <img
                  src={isDarkMode ? "/imgs/shield-solidDark.svg" : "/imgs/shield-solid.svg"}
                  alt=""
                  className=" mr-1 mt-1 h-4 w-5 scale-x-[-1] transform"
                />
                • {timeUntilShieldExpires}
              </div>
              <div className="mt-2 flex">
                <img
                  src={isDarkMode ? "/imgs/weightDark.svg" : "/imgs/weight.svg"}
                  alt=""
                  className=" mr-1 mt-1 h-4 w-5 scale-x-[-1] transform"
                />
                • {timeTrainingEnds}
              </div>
            </div>
          )}
          <MeterComponent
            startSec={pet.lastAttackUsed}
            endSec={pet.timeUntilAttack}
            imgSrc={isDarkMode ? "/imgs/swordDark.svg" : "/imgs/sword.svg"}
            imgClass="h-6 w-6 scale-x-[-1]"
            isDepleting={false}
            showTooltip={false}
            walletAddress={walletAddress}
            reverseTooltip={true}
            onClick={toggleTooltip}
            isDarkMode={isDarkMode}
          />
          <MeterComponent
            availableBonks={pet.availableBonks}
            imgSrc={isDarkMode ? "/imgs/boltDark.svg" : "/imgs/bolt.svg"}
            imgClass="h-5 w-5"
            walletAddress={walletAddress}
            showTooltip
            isDarkMode={isDarkMode}
          />
          <MeterComponent
            startSec={pet.lastWheelSpin}
            endSec={pet.timeUntilWheel}
            imgSrc={isDarkMode ? "/imgs/wheelspinDark.svg" : "/imgs/wheelspin.svg"}
            imgClass="h-6 w-6"
            walletAddress={walletAddress}
            showTooltip
            isDepleting={false}
            isDarkMode={isDarkMode}
          />
        </div>
      </div>
    </div>
  );
}
